/**
 * Entrada na sessão depois do cadastro (ou da verificação do e-mail).
 *
 * O cadastro é público e roda fora do `SessionProvider` em estado `ready`,
 * então não dá para usar `useSession().login` — a API já devolveu os tokens.
 * Grava-se direto no `sessionStore` e assume-se o contexto aqui mesmo (A003:
 * sempre `switch-context`, mesmo com um vínculo só), e a página recarrega
 * para a casca reconstruir a sessão do storage e abrir o portal.
 *
 * Se a troca de contexto falhar, os tokens do cadastro ficam gravados sem
 * contexto e a casca abre a escolha no boot — o mesmo caminho de uma sessão
 * interrompida no meio da escolha.
 */
import { decideEntry, registerApi, sessionApi, sessionStore, type AuthTokens, type PatientUser, type SessionContext } from '@eva/domain';
import { sameBrand } from '@eva/schemas';
import { limparDadosDeCadastro } from './RegisterContext';

/** Figura paterna não tem fase própria: cai em Conteúdos (regra 6 do guia). */
export function destinoAposCadastro(user: Pick<PatientUser, 'userType'>): string {
  return user.userType === 'FATHER_FIGURE' ? '/eu/content' : '/eu';
}

export async function entrarNaSessao(user: PatientUser, tokens: AuthTokens, destino?: string): Promise<void> {
  sessionStore.writeTokens(tokens);
  sessionStore.writeUser(user);

  try {
    const { contexts } = await sessionApi.listContexts();
    const decisao = decideEntry(contexts, sessionStore.rememberedFor(user.id));
    if (decisao.kind === 'direct') {
      const r = await sessionApi.switchContext(decisao.context);
      sessionStore.writeTokens({ accessToken: r.accessToken, refreshToken: r.refreshToken });
      const contexto: SessionContext = { ...r.context, brandName: decisao.context.brandName ?? r.context.brandName };
      sessionStore.writeContext(contexto);
      sessionStore.remember(user.id, contexto);
    }
  } catch {
    /* sem contexto gravado, a casca abre a escolha no boot */
  }

  limparDadosDeCadastro();
  // Conta com onboarding pendente (A062, fase C) vai para o onboarding,
  // não para a home: sem momento, ela seria lida como gestante.
  window.location.assign(destino ?? (user.onboardingPendente ? '/cadastro/onboarding' : destinoAposCadastro(user)));
}

/**
 * Link da empresa guardado do cadastro, para quem descobriu que já tinha
 * conta e entrou com a senha (A062): aplica depois do login e troca os
 * tokens, porque a sessão carrega a empresa. Nunca lança — falhar aqui não
 * pode impedir a pessoa de entrar.
 */
export async function aplicarEmpresaPendente(): Promise<void> {
  let empresa: { codigo: string; nome: string; comprovanteCorporativo?: string } | undefined;
  try {
    empresa = JSON.parse(sessionStorage.getItem('eva_cadastro') ?? '{}')?.empresa;
  } catch {
    return;
  }
  if (!empresa?.codigo) return;
  try {
    const r = await registerApi.aplicarEmpresa(empresa);
    sessionStore.writeTokens(r.tokens);
  } catch {
    /* o vínculo com a empresa pode ser feito depois pelo link de novo */
  }
  limparDadosDeCadastro();
}

/**
 * Quem preenche o cadastro com o e-mail e a senha de uma conta que já é
 * paciente desta marca entra direto, sem código (A083): esqueceu que tinha
 * conta, mas lembrou a senha — e a senha certa já prova que a conta é dela.
 *
 * Usa o login de sempre, com os mesmos limites de tentativa e a mesma
 * resposta para conta inexistente e senha errada (A069). Devolve `false` —
 * e o cadastro segue para o código — quando o login não passa ou quando a
 * conta não é paciente desta marca: a equipe da marca e a paciente de outra
 * marca têm caminho próprio depois do código (A032, A052).
 */
export async function entrarComASenhaDoCadastro(email: string, senha: string, slugDaPorta: string | null | undefined, marcaDaPorta: string): Promise<boolean> {
  let login: Awaited<ReturnType<typeof sessionApi.login>>;
  try {
    login = await sessionApi.login(email, senha, slugDaPorta);
  } catch {
    return false;
  }
  sessionStore.writeTokens(login);
  try {
    const { contexts } = await sessionApi.listContexts();
    const comoPaciente = contexts.find((c) => c.role === 'PATIENT' && sameBrand(c.brandId, marcaDaPorta));
    if (!comoPaciente) throw new Error('não é paciente desta marca');
    const r = await sessionApi.switchContext(comoPaciente);
    sessionStore.writeTokens({ accessToken: r.accessToken, refreshToken: r.refreshToken });
    sessionStore.writeUser(login.user);
    const contexto: SessionContext = { ...r.context, brandName: comoPaciente.brandName ?? r.context.brandName };
    sessionStore.writeContext(contexto);
    sessionStore.remember(login.user.id, contexto);
  } catch {
    await sessionApi.logout(login.refreshToken);
    sessionStore.clear();
    return false;
  }
  // O link da empresa, se ela chegou por um (A062).
  await aplicarEmpresaPendente();
  limparDadosDeCadastro();
  const user = login.user as unknown as PatientUser;
  window.location.assign(user.onboardingPendente ? '/cadastro/onboarding' : destinoAposCadastro(user));
  return true;
}
